/**
 * How-to, effort, and Sponsor vendor choice for one chore.
 * CommonJS only. No object spread.
 */

const { stripTaskEmoji } = require('./taskEmoji');
const { isAcFilterTask, readFilterSizes } = require('./homeFacts');
const { addCalendarDays, localDateString } = require('../utils/dates');
const { tradeForTaskCategory, vendorsMatchingTrade } = require('./vendorTrades');

const EFFORT_QUICK = '5-minute job';
const EFFORT_WEEKEND = 'Weekend project';
const EFFORT_PRO = 'Call a pro';

function libraryOf(task) {
  return (task && task.task) || task || {};
}

function minuteCount(time) {
  const match = String(time || '').toLowerCase().match(/(\d+)\s*minute/);
  return match ? Number(match[1]) : 0;
}

function isLookOnly(task) {
  const library = libraryOf(task);
  const title = String(library.title || library.task_name || task.task_name || '').toLowerCase();
  const summary = String(library.description || '').toLowerCase().split('\n')[0];
  return /\blook\b|\bwalk around\b|from the yard|visual check/.test(title + ' ' + summary);
}

function effortLabel(task) {
  const library = libraryOf(task);
  const time = String(library.estimated_time || '').toLowerCase();
  const difficulty = String(library.difficulty_level || '').toLowerCase();
  const minutes = minuteCount(time);
  const proVisit = /pro visit/.test(time) || difficulty === 'hard';
  if (proVisit && !isLookOnly(task)) return EFFORT_PRO;
  if (/hour|weekend|afternoon/.test(time) || difficulty === 'medium' || minutes >= 30) return EFFORT_WEEKEND;
  if (minutes >= 8) return minutes + '-minute job';
  return EFFORT_QUICK;
}

function cleanLine(value) {
  return stripTaskEmoji(value).replace(/^\s*\d+[.)]\s*/, '').replace(/\s+/g, ' ').trim();
}

function propertyTaxSteps(title) {
  const text = String(title || '').toLowerCase();
  if (/trim|property tax assessment/.test(text)) {
    return [
      'Open the TRIM notice from your county.',
      'Check the proposed value and the tax amount.',
      'Note the hearing date if you want to question the value.',
      'Save the notice with this home.',
    ];
  }
  if (/early-pay|early pay/.test(text)) {
    return [
      'Open the property tax bill from your county.',
      'Check the discount for paying by this date.',
      'Pay on the county site if you want that discount.',
      'Save the receipt with this home.',
    ];
  }
  if (/discount last chance/.test(text)) {
    return [
      'Open the property tax bill from your county.',
      'Check the smaller discount that is still available.',
      'Pay on the county site if you want that discount.',
      'Save the receipt with this home.',
    ];
  }
  if (/property tax final|tax is due in full/.test(text)) {
    return [
      'Open the property tax bill from your county.',
      'Pay the remaining balance by this date.',
      'Save the receipt with this home.',
    ];
  }
  if (/homestead/.test(text)) {
    return [
      'Open your county property appraiser site.',
      'Check whether a homestead exemption is already on this home.',
      'File or update it before the deadline if you live here.',
      'Save the filing with this home.',
    ];
  }
  return null;
}

function stepsFrom(task) {
  const library = libraryOf(task);
  const title = library.title || library.task_name || (task && task.task_name) || '';
  const written = propertyTaxSteps(title);
  if (written) return written;
  const source = String(library.diy_tips || '');
  const lines = source.split(/\n+/).map(cleanLine).filter((line) => line && !/^supplies:/i.test(line));
  const steps = [];
  lines.forEach((line) => {
    if (steps.length >= 4) return;
    if (line.length > 180) return;
    steps.push(line);
  });
  return steps.slice(0, 4);
}

function taskSummaryText(text) {
  const raw = String(text || '').trim();
  if (!raw) return '';
  const kept = [];
  raw.split(/\n\s*\n/).forEach((chunk) => {
    const lines = chunk.split(/\n/).map((line) => line.trim()).filter(Boolean);
    const numbered = lines.length > 0 && lines.every((line) => /^\d+[.)]\s+/.test(line));
    if (!numbered) kept.push(lines.join(' '));
  });
  return kept.join('\n\n').trim();
}

function supplyPhrase(task, home) {
  const library = libraryOf(task);
  if (isAcFilterTask(library) || isAcFilterTask(task)) {
    const sizes = readFilterSizes(home).filter(Boolean);
    if (sizes.length === 1) return 'a ' + sizes[0] + ' filter';
    if (sizes.length > 1) {
      return sizes.map((size) => 'a ' + size + ' filter').join(' and ');
    }
  }
  const source = String(library.diy_tips || library.description || '');
  const match = source.split(/\n+/).map(cleanLine).find((line) => /^supplies:/i.test(line));
  if (match) return match.replace(/^supplies:\s*/i, '');
  return '';
}

function taskHowTo(task, home) {
  const library = libraryOf(task);
  const steps = stepsFrom(task);
  const supplies = supplyPhrase(task, home);
  const minutes = stripTaskEmoji(library.estimated_time) || 'A short visit';
  const time = supplies ? minutes + ', needs ' + supplies : minutes;
  return {
    steps,
    supplies: supplies || 'Nothing extra',
    time,
    effort: effortLabel(task),
  };
}

function sponsorVendorForTask(task, vendors) {
  if (effortLabel(task) !== EFFORT_PRO) return null;
  const library = libraryOf(task);
  const trade = tradeForTaskCategory(library.category || task.category);
  const matches = vendorsMatchingTrade(vendors, trade);
  return matches[0] || null;
}

function snoozeUntil(days, today) {
  const count = days === 30 ? 30 : 7;
  return addCalendarDays(localDateString(today || new Date()), count);
}

module.exports = {
  EFFORT_QUICK,
  EFFORT_WEEKEND,
  EFFORT_PRO,
  effortLabel,
  taskSummaryText,
  taskHowTo,
  sponsorVendorForTask,
  snoozeUntil,
};
